import { useEffect, useState, useCallback } from "react";
import { Camera, X, ChevronLeft, ChevronRight } from "lucide-react";
import { useContent, resolveImage } from "../contexts/ContentContext";

// Masonry span pattern cycled across items to keep visual rhythm.
const SPAN_PATTERN = [
  "md:col-span-6 md:row-span-2",
  "md:col-span-3",
  "md:col-span-3",
  "md:col-span-3",
  "md:col-span-3",
  "md:col-span-3",
  "md:col-span-3",
  "md:col-span-3",
  "md:col-span-3",
  "md:col-span-3",
  "md:col-span-3",
  "md:col-span-3",
  "md:col-span-3",
  "md:col-span-6",
  "md:col-span-6",
];

export const Gallery = () => {
  const { data: gallery } = useContent("gallery");
  const [index, setIndex] = useState(null); // currently lightboxed image index, or null
  const images = gallery?.images || [];

  const close = useCallback(() => setIndex(null), []);
  const next = useCallback(() => {
    setIndex((i) => (i === null ? null : (i + 1) % images.length));
  }, [images.length]);
  const prev = useCallback(() => {
    setIndex((i) => (i === null ? null : (i - 1 + images.length) % images.length));
  }, [images.length]);

  // Keyboard nav + Esc + body scroll lock while open
  useEffect(() => {
    if (index === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [index, close, next, prev]);

  if (!gallery) return null;

  const current = index !== null ? images[index] : null;

  return (
    <>
      <section id="gallery" data-testid="gallery-section" className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex items-end justify-between flex-wrap gap-6">
            <div>
              <span className="section-eyebrow">
                <Camera className="w-3.5 h-3.5" />
                {gallery.eyebrow}
              </span>
              <h2 data-testid="gallery-heading" className="mt-5 font-display text-3xl md:text-5xl font-bold tracking-tight text-slate-900">
                {gallery.heading?.split("impact")[0] || gallery.heading}
                {gallery.heading?.includes("impact") && <span className="text-gradient-gold">impact</span>}
                {gallery.heading?.includes("impact") && gallery.heading.split("impact")[1]}
              </h2>
            </div>
            <p className="text-base text-slate-600 max-w-md">{gallery.intro}</p>
          </div>

          <div className="mt-12 grid grid-cols-2 md:grid-cols-12 auto-rows-[200px] md:auto-rows-[240px] gap-4 md:gap-5">
            {images.map((t, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setIndex(i)}
                data-testid={`gallery-tile-${i}`}
                data-fade
                style={{ transitionDelay: `${(i % 3) * 80}ms` }}
                className={`gallery-item ${SPAN_PATTERN[i % SPAN_PATTERN.length]} cursor-zoom-in`}
                aria-label={`Open ${t.caption || `gallery image ${i + 1}`} full size`}
              >
                <img src={resolveImage(t.url)} alt={t.caption || `Gallery image ${i + 1}`} loading="lazy" style={{ objectPosition: "center 30%" }} />
                {t.caption && (
                  <div className="gallery-overlay">
                    <div>
                      <p className="mt-1 font-display text-lg md:text-xl font-bold leading-tight">{t.caption}</p>
                    </div>
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox overlay */}
      {current && (
        <div
          data-testid="gallery-lightbox"
          className="fixed inset-0 z-[100] bg-black/92 backdrop-blur-sm flex items-center justify-center p-4 md:p-8 animate-fade-in"
          onClick={close}
          role="dialog"
          aria-modal="true"
          aria-label="Photo viewer"
        >
          {/* Close */}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); close(); }}
            data-testid="lightbox-close"
            className="absolute top-4 right-4 md:top-6 md:right-6 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white grid place-items-center transition-colors backdrop-blur"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Counter */}
          <div className="absolute top-4 left-4 md:top-6 md:left-6 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur text-xs font-bold text-white tracking-wider" data-testid="lightbox-counter">
            {index + 1} / {images.length}
          </div>

          {/* Prev */}
          {images.length > 1 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); prev(); }}
              data-testid="lightbox-prev"
              className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white grid place-items-center transition-colors backdrop-blur"
              aria-label="Previous image"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
          )}

          {/* Next */}
          {images.length > 1 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); next(); }}
              data-testid="lightbox-next"
              className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white grid place-items-center transition-colors backdrop-blur"
              aria-label="Next image"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          )}

          {/* Image */}
          <img
            src={resolveImage(current.url)}
            alt={current.caption || `Gallery image ${index + 1}`}
            data-testid="lightbox-image"
            onClick={(e) => e.stopPropagation()}
            className="max-w-[95vw] max-h-[90vh] object-contain rounded-lg shadow-[0_40px_120px_rgba(0,0,0,0.6)]"
          />
        </div>
      )}
    </>
  );
};
